/**
 * CorrectionDialog — Phase C2 (decision 5b).
 *
 * Opens on an approved, locked week for Payroll. The locked hours are read
 * through the approved-hours contract (never the grid), shown with the impact
 * first — which pay runs, exports and invoices this would touch. Today those are
 * empty; the panel is wired so it fills itself in once payroll and invoicing
 * exist. Payroll edits the corrected totals, opens the correction with a reason,
 * and a DIFFERENT second approver confirms it. The original is never edited; the
 * correction rides on top of it.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { AlertTriangle, CheckCircle2, History } from 'lucide-react';
import { schedulingAPI } from '../../lib/api';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';

export function CorrectionDialog({ week, open, onOpenChange, onChanged }) {
  const perms = useEffectivePermissions();
  const [impact, setImpact] = useState(null);
  const [rows, setRows] = useState([]);          // {shift_day_id, ...original, corrected_total}
  const [reason, setReason] = useState('');
  const [corrections, setCorrections] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!week?.id) return;
    try {
      const [{ data: imp }, { data: ah }, { data: list }] = await Promise.all([
        schedulingAPI.correctionImpact(week.id),
        schedulingAPI.approvedHours({ job_id: week.job_id, week_start_date: week.week_start_date }),
        schedulingAPI.listCorrections(week.id),
      ]);
      setImpact(imp);
      const mine = (ah.records || []).filter(
        (r) => r.schedule_week_id === week.id && r.source === 'schedule'
          && !r.superseded_by_correction_id);
      setRows(mine.map((r) => ({ ...r, corrected_total: r.total_hours })));
      setCorrections(list.corrections || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load correction data');
    }
  };
  useEffect(() => { if (open) { setReason(''); load(); } /* eslint-disable-next-line */ }, [open]);

  const changed = rows.filter((r) => Number(r.corrected_total) !== Number(r.total_hours));

  const doOpen = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    if (!changed.length) { toast.error('Change at least one shift to correct'); return; }
    setBusy(true);
    try {
      await schedulingAPI.openCorrection(week.id, {
        reason: reason.trim(),
        lines: changed.map((r) => ({ shift_day_id: r.shift_day_id,
          total_hours: Number(r.corrected_total) })),
      });
      toast.success('Correction opened — a second approver must confirm it');
      setReason('');
      load();
      // No parent reload here: opening a correction does not change the week's
      // status/lock, and reloading would remount this dialog and hide the list
      // the user just created. The parent refreshes on confirm, below.
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open the correction');
    } finally { setBusy(false); }
  };

  const confirm = async (id) => {
    setBusy(true);
    try {
      await schedulingAPI.confirmCorrection(id, 'confirmed');
      toast.success('Correction confirmed');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm');
    } finally { setBusy(false); }
  };

  const impactEmpty = impact
    && !impact.pay_runs?.length && !impact.exports?.length && !impact.invoices?.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle>Correct locked hours</DialogTitle>
          <DialogDescription>
            The approved hours are locked. A correction is added on top — the original stays on
            record and a second approver must confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* impact first */}
          <div className="rounded-md border border-line p-3" data-testid="correction-impact">
            <div className="text-sm font-medium flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> What this touches
            </div>
            {!impact ? <p className="text-xs text-fg-muted mt-2">Checking…</p> : impactEmpty ? (
              <p className="text-xs text-fg-muted mt-2" data-testid="correction-impact-none">
                None yet — no pay run, export or invoice has used these hours.
              </p>
            ) : (
              <ul className="text-xs text-fg-muted mt-2 space-y-1">
                <li>Pay runs: {impact.pay_runs.length}</li>
                <li>Exports: {impact.exports.length}</li>
                <li>Invoices: {impact.invoices.length}
                  {impact.requires_finance && (
                    <Badge variant="outline"
                      className="ml-2 bg-amber-500/10 text-amber-600 border-amber-500/30">
                      Finance approval required
                    </Badge>)}
                </li>
              </ul>
            )}
          </div>

          {/* editable rows */}
          <div className="rounded-md border border-line overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken text-fg-muted">
                <tr>
                  <th className="text-left px-3 py-2">Officer</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Locked</th>
                  <th className="text-right px-3 py-2">Corrected</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.shift_day_id} className="border-t border-line"
                    data-testid="correction-edit-row">
                    <td className="px-3 py-2">{r.employee_number || r.employee_id}</td>
                    <td className="px-3 py-2 tabular-nums">{r.shift_date}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.total_hours}</td>
                    <td className="px-3 py-2 text-right">
                      <Input type="number" step="0.25" value={r.corrected_total}
                        data-testid={`correction-hours-${r.shift_day_id}`}
                        onChange={(e) => setRows((rs) => rs.map((x) =>
                          x.shift_day_id === r.shift_day_id
                            ? { ...x, corrected_total: e.target.value } : x))}
                        className="w-24 text-right inline-block" />
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-fg-muted">
                    No locked hours on this week.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being corrected?" data-testid="correction-reason" />

          {/* existing corrections */}
          {corrections.length > 0 && (
            <div className="rounded-md border border-line p-3 space-y-2" data-testid="correction-list">
              <div className="text-sm font-medium flex items-center gap-1.5">
                <History className="h-4 w-4" /> Corrections on this week
              </div>
              {corrections.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-xs"
                  data-testid="correction-list-row">
                  <span>
                    {c.status === 'confirmed' ? (
                      <span className="text-emerald-600 inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
                      </span>
                    ) : <Badge variant="outline">{c.status}</Badge>}
                    {' '}· {c.lines?.length} shift(s) · {c.reason}
                  </span>
                  {c.status === 'open' && c.opened_by !== perms.user_id && (
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => confirm(c.id)}
                      data-testid={`correction-confirm-${c.id}`}>Confirm</Button>
                  )}
                  {c.status === 'open' && c.opened_by === perms.user_id && (
                    <span className="text-fg-muted">Needs a second approver</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={doOpen} disabled={busy || !changed.length}
            data-testid="correction-open-btn">
            {busy ? 'Working…' : `Open correction (${changed.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CorrectionDialog;
